import type { GroupingSource } from "services/metadoc-api";
import type { MetadocSearchIndexVersionStatus } from "services/metadoc-search-api";

/*
 * centralizes metadoc search version suffix rules
 * for requested search scope and status lookup scope.
 */
export const METADOC_SEARCH_VERSION_SUFFIX = {
  PRE_AI: "_pre_ai",
  POST_AI: "_post_ai",
  LEGACY_POST_AI: "",
} as const;

export type RequestedMetadocSearchVersionSuffix =
  (typeof METADOC_SEARCH_VERSION_SUFFIX)[keyof typeof METADOC_SEARCH_VERSION_SUFFIX];

export const getRequestedMetadocSearchVersionSuffix = ({
  groupingSource,
  hasPreAiRunDownloadUrl,
}: {
  groupingSource: GroupingSource;
  hasPreAiRunDownloadUrl: boolean;
}): RequestedMetadocSearchVersionSuffix =>
  hasPreAiRunDownloadUrl && groupingSource !== "OPS"
    ? METADOC_SEARCH_VERSION_SUFFIX.POST_AI
    : METADOC_SEARCH_VERSION_SUFFIX.PRE_AI;

export const resolveMetadocSearchVersionContext = ({
  requestedVersionSuffix,
  versions,
}: {
  requestedVersionSuffix: RequestedMetadocSearchVersionSuffix;
  versions: MetadocSearchIndexVersionStatus[];
}) => {
  const hasRequestedVersion = versions.some(
    (version) => version.version_suffix === requestedVersionSuffix,
  );
  const hasLegacyVersion = versions.some(
    (version) =>
      version.version_suffix === METADOC_SEARCH_VERSION_SUFFIX.LEGACY_POST_AI,
  );

  // Fall back to the legacy unscoped index if the requested version
  // doesn't exist yet (handles indexes created before versioned suffixes).
  const shouldFallbackToLegacy = !hasRequestedVersion && hasLegacyVersion;

  return {
    resolvedVersionSuffix: shouldFallbackToLegacy
      ? undefined
      : requestedVersionSuffix,
    statusVersionSuffix: shouldFallbackToLegacy
      ? METADOC_SEARCH_VERSION_SUFFIX.LEGACY_POST_AI
      : requestedVersionSuffix,
  };
};
